import React, { useEffect, useReducer } from 'react';
import { useSelector } from 'react-redux';

import useSearch from '../../hooks/useSearch';
import { useTabs } from '../../hooks/useTabs';
import { useSaveSettings } from '../../hooks/useSaveSettings';
import { CATEGORIES, SUBCATEGORIES } from '../../common/categories';
import { fieldsReducer, initFieldsState, mapToSettings } from './integrationsReducer';

import Category from '../../components/Category/Category';
import ADFSApps from '../../components/FederatedApps/ADFSApps';
import PingFedApps from '../../components/FederatedApps/PingFedApps';
import AzureConnectors from '../../components/Connectors/AzureConnectors';
import NewAzureConnectors from '../../components/Connectors/NewAzureConnectors';
import OktaConnectors from '../../components/Connectors/OktaConnectors';
import { httpPost, isUserPermitted } from '../../../utils';
import { ROLES, SHARED_KEY, USER_ENGAGEMENT_PAGE_KEYS } from '@silverfort-nodelibs/common/utils/consts';
import SSOApps from '../../components/SSOApps/SSOApps';
import { useVisibleTabs } from '../../hooks/lockedFeatures/useVisibleTabs';

const { INTEGRATIONS } = CATEGORIES;
const { ADFS_FEDERATED_APPS, PING_FEDERATED_APPS, AZURE_CONNECTORS, NEW_AZURE_CONNECTORS, OKTA_CONNECTORS, SSO_APPS } =
    SUBCATEGORIES;

const Integrations = ({ saveFunc, disableSave }) => {
    const settings = useSelector((state) => state.settings.settings);
    const user = useSelector((state) => state.userData.user);

    const [fields, dispatch] = useReducer(fieldsReducer, { settings }, initFieldsState);
    const visibleFields = useSearch(useVisibleTabs(fields));
    const [selectedTab, setSelectedTab] = useTabs(INTEGRATIONS, visibleFields);
    const [save, discard] = useSaveSettings(saveFunc, settings, mapToSettings, fields, selectedTab, dispatch);

    const authorizePermission = isUserPermitted(user, 'authorize', ROLES.EDITOR.value);

    let content = null;
    if (visibleFields[ADFS_FEDERATED_APPS.ID] && selectedTab === ADFS_FEDERATED_APPS.ID) {
        content = (
            <ADFSApps
                fields={visibleFields[ADFS_FEDERATED_APPS.ID].fields}
                dispatch={dispatch}
                authorizePermission={authorizePermission}
            />
        );
    } else if (visibleFields[PING_FEDERATED_APPS.ID] && selectedTab === PING_FEDERATED_APPS.ID) {
        content = (
            <PingFedApps
                fields={visibleFields[PING_FEDERATED_APPS.ID].fields}
                dispatch={dispatch}
                authorizePermission={authorizePermission}
            />
        );
    } else if (visibleFields[AZURE_CONNECTORS.ID] && selectedTab === AZURE_CONNECTORS.ID) {
        // Not supported by locked feature mechanism. Should be deleted in the future.
        content = (
            <AzureConnectors
                fields={visibleFields[AZURE_CONNECTORS.ID].fields}
                dispatch={dispatch}
                authorizePermission={authorizePermission}
            />
        );
    } else if (visibleFields[NEW_AZURE_CONNECTORS.ID] && selectedTab === NEW_AZURE_CONNECTORS.ID) {
        content = (
            <NewAzureConnectors
                fields={visibleFields[NEW_AZURE_CONNECTORS.ID].fields}
                dispatch={dispatch}
                authorizePermission={authorizePermission}
            />
        );
    } else if (visibleFields[OKTA_CONNECTORS.ID] && selectedTab === OKTA_CONNECTORS.ID) {
        content = (
            <OktaConnectors
                fields={visibleFields[OKTA_CONNECTORS.ID].fields}
                dispatch={dispatch}
                authorizePermission={authorizePermission}
            />
        );
    } else if (visibleFields[SSO_APPS.ID] && selectedTab === SSO_APPS.ID) {
        content = (
            <SSOApps
                ssoVendors={visibleFields[SSO_APPS.ID][SHARED_KEY].ssoVendors}
                fields={visibleFields[SSO_APPS.ID].fields}
                ssoApplicationToRuleDetails={settings.ssoApplicationToRuleDetails}
                dispatch={dispatch}
                authorizePermission={authorizePermission}
            />
        );
    }

    useEffect(() => {
        httpPost('/data/userEngagement', { pageId: USER_ENGAGEMENT_PAGE_KEYS.SETTINGS_INTEGRATION });
    }, []);

    return (
        <Category
            category={INTEGRATIONS}
            fields={fields}
            visibleFields={visibleFields}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            saveSettings={save}
            discard={() => discard(false)}
            discardAll={() => discard(true)}
            disableSave={disableSave}>
            {content}
        </Category>
    );
};

export default Integrations;
